import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Lock, TriangleAlert, ArrowRight } from 'lucide-react';
import { usePlan } from '../../context/PlanContext';

/**
 * PlanGate — wraps page content, in the Olli design.
 * If the module is not in the plan: the page sits behind a blur and a centred
 * upgrade card. The page behind is held to one screen's height, so the card
 * is always centred in view however long the page is (a tall page used to
 * push it off screen). The side menu stays usable.
 * If at limit: a warning banner above the content.
 */
const NAMES = {
    ai_agents_chat: 'AI Chat Agent',
    ai_agents_voice: 'AI Voice Agent',
    knowledge_base: 'Knowledge Base',
    integrations: 'Integrations',
    marketing_suite: 'Marketing Suite',
    whatsapp: 'WhatsApp Business',
    support: 'Support Desk',
    users: 'Team Members',
    patients: 'Patients',
    appointments: 'Appointments',
    treatment_plans: 'Treatment Plans',
    schedules: 'Doctor Schedules',
};

const FEATURES = {
    ai_agents_chat: ['24/7 AI chat on your website', 'Instant patient queries handling', 'Multi-language support', 'Automated appointment booking'],
    ai_agents_voice: ['AI voice calls for follow-ups', 'IVR automation', 'Natural voice conversations', 'Exotel / Twilio integration'],
    knowledge_base: ['Upload clinic policies & FAQs', 'AI-powered Q&A', 'Multi-document search', 'Semantic search'],
    integrations: ['Meta Lead Ads sync', 'Google Ads integration', 'Webhook management', 'Third-party API access'],
    marketing_suite: ['Performance dashboard with Page insights', 'AI-generated ad campaigns', 'Lead form builder + audience manager', 'Comments moderation + Social Inbox (Messenger / IG Direct)', 'Comment-to-DM automations', 'Competitor ad research'],
    whatsapp: ['Official WABA messaging', 'Bulk campaigns', 'Template management', 'Auto-replies'],
    support: ['Ticket management', 'Customer complaints', 'Feedback tracking', 'Category-based routing'],
    users: ['Unlimited team members', 'Role-based permissions', 'Activity audit trail'],
};

const PlanGate = ({ module, children }) => {
    const plan = usePlan();
    const navigate = useNavigate();

    if (!plan || plan.loading) return children;

    const hasAccess = plan.hasModule(module);
    const atLimit = plan.isAtLimit(module);
    const usage = plan.getUsage(module);
    const subscription = plan.getSubscription();

    // `module` may be a comma-separated list (access if ANY is in the plan).
    // Copy/features below are keyed off the first slug.
    const primaryModule = String(module).split(',')[0].trim();
    const moduleDisplayName = NAMES[primaryModule] || primaryModule.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    const features = FEATURES[primaryModule] || [];

    // Module not in plan — blurred page behind, upgrade card in front
    if (!hasAccess) {
        return (
            <div className="olli relative isolate h-[calc(100vh-2rem)] min-h-[560px] overflow-hidden rounded-xl font-sans text-ink">
                <div aria-hidden className="pointer-events-none h-full select-none overflow-hidden opacity-40 blur-[8px] saturate-[1.2]">
                    {children}
                </div>

                <div className="absolute inset-0 z-20 flex items-center justify-center overflow-y-auto bg-ink/20 p-5">
                    <section
                        role="dialog"
                        aria-modal="false"
                        aria-labelledby="plan-gate-title"
                        className="my-auto flex w-[520px] max-w-full animate-nav-in flex-col overflow-hidden rounded-xl bg-paper shadow-lift ring-1 ring-line"
                    >
                        <header className="relative overflow-hidden bg-ink bg-[image:var(--gradient-night)] px-8 pb-8 pt-8 text-paper">
                            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-sun text-ink">
                                <Lock size={20} strokeWidth={1.75} />
                            </span>
                            <p className="mt-5 text-caption font-semibold uppercase tracking-wider text-sun">Not in your plan</p>
                            <h2 id="plan-gate-title" className="mt-1 text-h3 font-semibold">Unlock {moduleDisplayName}</h2>
                            <p className="mt-1.5 text-small text-paper/75">
                                Not included in your <strong className="font-semibold text-paper">{subscription?.plan_name || 'current'}</strong> plan. Upgrade to unlock this and more.
                            </p>
                        </header>

                        <div className="flex flex-col gap-6 px-8 py-7">
                            {features.length > 0 && (
                                <div>
                                    <h3 className="text-caption font-semibold uppercase tracking-wider text-muted">What you'll get</h3>
                                    <ul className="mt-3 flex flex-col gap-2.5">
                                        {features.map((feat) => (
                                            <li key={feat} className="flex items-start gap-3 text-small">
                                                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sun text-ink">
                                                    <Check size={12} strokeWidth={2.5} />
                                                </span>
                                                {feat}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => navigate(-1)}
                                    className="h-12 shrink-0 rounded-full bg-mist px-6 text-small font-semibold text-ink hover:bg-line"
                                >
                                    Not now
                                </button>
                                <button
                                    type="button"
                                    onClick={() => navigate('/subscription/plans')}
                                    className="flex h-12 flex-1 items-center justify-center gap-2 rounded-full bg-sun px-6 text-body font-semibold text-ink hover:brightness-95"
                                >
                                    Upgrade plan
                                    <ArrowRight size={16} strokeWidth={2} />
                                </button>
                            </div>

                            <p className="text-center text-caption text-muted">Instant activation · Cancel anytime · No hidden fees</p>
                        </div>
                    </section>
                </div>
            </div>
        );
    }

    // At limit — show warning banner above content
    if (atLimit && usage) {
        return (
            <div>
                <div className="olli mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md bg-warning-soft px-5 py-3.5 font-sans text-warning">
                    <div className="flex items-center gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-paper/70">
                            <TriangleAlert size={18} strokeWidth={1.75} />
                        </span>
                        <div>
                            <div className="text-small font-semibold">Limit reached</div>
                            <div className="text-caption">{usage.used} of {usage.limit} {usage.name || primaryModule} used</div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => navigate('/subscription/plans')}
                        className="flex h-10 items-center gap-1.5 rounded-full bg-ink px-5 text-small font-semibold text-paper hover:bg-ink/85"
                    >
                        Upgrade
                        <ArrowRight size={14} strokeWidth={2} />
                    </button>
                </div>
                {children}
            </div>
        );
    }

    // Has access + within limits — render normally
    return children;
};

export default PlanGate;
